import { createContext, useContext, type ReactNode } from 'react';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** Supporting text under the title. */
  description?: ReactNode;
  /**
   * Auto-dismiss delay in ms. `null` keeps the toast until dismissed. Defaults: 5000 for
   * info/success/warning; errors persist until dismissed.
   */
  duration?: number | null;
  action?: ToastAction;
  /** Stable id: showing a toast with an existing id replaces it (deduplication). */
  id?: string;
}

export interface ToastRecord extends ToastOptions {
  id: string;
  tone: ToastTone;
  title: ReactNode;
}

export interface ToastApi {
  show: (tone: ToastTone, title: ReactNode, options?: ToastOptions) => string;
  success: (title: ReactNode, options?: ToastOptions) => string;
  error: (title: ReactNode, options?: ToastOptions) => string;
  info: (title: ReactNode, options?: ToastOptions) => string;
  warning: (title: ReactNode, options?: ToastOptions) => string;
  dismiss: (id?: string) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast() must be used inside <ToastProvider>.');
  return api;
}
